import type {
  CreateRuleDefinitionDto,
  CreateRuleTargetDto,
  UpdateRuleDefinitionDto,
  UpdateRuleTargetDto,
  UpdateSettingsDto,
} from '@citilight-monitor/contracts';
import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth';
import { rangeKey, resolveRange, type RangeSelection } from '../time/dateRange';

/** Dashboards stay current without a manual reload. */
const LIVE_REFRESH_MS = 60_000;

export function useOverview() {
  const { api } = useAuth();
  return useQuery({ queryKey: ['overview'], queryFn: () => api.overview(), refetchInterval: LIVE_REFRESH_MS });
}

export function useClientStatus(clientId: string) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ['client', clientId],
    queryFn: () => api.client(clientId),
    refetchInterval: LIVE_REFRESH_MS,
  });
}

export function useSeries(clientId: string, selection: RangeSelection) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ['series', clientId, rangeKey(selection)],
    queryFn: () => api.series(clientId, resolveRange(selection)),
    placeholderData: keepPreviousData,
    refetchInterval: selection.kind === 'preset' ? LIVE_REFRESH_MS : false,
  });
}

export function useAlerts(clientId: string, selection: RangeSelection) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ['alerts', clientId, rangeKey(selection)],
    queryFn: () => api.alerts(clientId, resolveRange(selection)),
    placeholderData: keepPreviousData,
    refetchInterval: selection.kind === 'preset' ? LIVE_REFRESH_MS : false,
  });
}

const LAST_24H: RangeSelection = { kind: 'preset', preset: '24h' };

/**
 * Series for every client over the given range. Defaults to the last 24 hours for the
 * overview's sparklines. Shares its cache entries with useSeries, so opening a client
 * after the overview reuses the data.
 */
export function useFleetSeries(clientIds: string[], selection: RangeSelection = LAST_24H) {
  const { api } = useAuth();
  return useQueries({
    queries: clientIds.map((id) => ({
      queryKey: ['series', id, rangeKey(selection)],
      queryFn: () => api.series(id, resolveRange(selection)),
      placeholderData: keepPreviousData,
      refetchInterval: selection.kind === 'preset' ? LIVE_REFRESH_MS : false,
    })),
  });
}

/** Alerts sent in the last 24 hours, for every client (one request per client; the API has no fleet-wide endpoint). */
export function useFleetAlerts(clientIds: string[]) {
  const { api } = useAuth();
  return useQueries({
    queries: clientIds.map((id) => ({
      queryKey: ['alerts', id, rangeKey(LAST_24H)],
      queryFn: () => api.alerts(id, resolveRange(LAST_24H)),
      refetchInterval: LIVE_REFRESH_MS,
    })),
  });
}

export function useRuleTargets() {
  const { api } = useAuth();
  return useQuery({ queryKey: ['ruleTargets'], queryFn: () => api.ruleTargets() });
}

export function useDiscoveredMetrics(ruleTargetId: string | undefined) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ['discoveredMetrics', ruleTargetId],
    queryFn: () => api.discoverMetrics(ruleTargetId!),
    enabled: Boolean(ruleTargetId),
    retry: false,
  });
}

export function useRules(ruleTargetId?: string) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ['rules', ruleTargetId ?? 'all'],
    queryFn: () => api.rules(ruleTargetId),
    placeholderData: keepPreviousData,
  });
}

export function useStoredMetrics(ruleTargetId: string | undefined) {
  const { api } = useAuth();
  return useQuery({
    queryKey: ['storedMetrics', ruleTargetId],
    queryFn: () => api.storedMetrics(ruleTargetId!),
    enabled: Boolean(ruleTargetId),
  });
}

export function useUpdateMetricLabel() {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleTargetId, metricId, label }: { ruleTargetId: string; metricId: string; label: string }) =>
      api.updateMetricLabel(ruleTargetId, metricId, label),
    onSuccess: (_, { ruleTargetId }) => {
      void queryClient.invalidateQueries({ queryKey: ['storedMetrics', ruleTargetId] });
      void queryClient.invalidateQueries({ queryKey: ['rules'] });
    },
  });
}

export function useCreateRuleTarget() {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRuleTargetDto) => api.createRuleTarget(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ruleTargets'] }),
  });
}

export function useUpdateRuleTarget() {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateRuleTargetDto }) => api.updateRuleTarget(id, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ruleTargets'] }),
  });
}

export function useDeleteRuleTarget() {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteRuleTarget(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ruleTargets'] });
      void queryClient.invalidateQueries({ queryKey: ['rules'] });
    },
  });
}

export function useCreateRule() {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRuleDefinitionDto) => api.createRule(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rules'] }),
  });
}

export function useUpdateRule() {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateRuleDefinitionDto }) => api.updateRule(id, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rules'] }),
  });
}

export function useDeleteRule() {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteRule(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rules'] }),
  });
}

export function useSettings() {
  const { api } = useAuth();
  return useQuery({ queryKey: ['settings'], queryFn: () => api.settings() });
}

export function useUpdateSettings() {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSettingsDto) => api.updateSettings(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
  });
}
